import { formatPositionCode, mapSubscription } from "@components/PositionsPayRangesPage/mapPositionSubscriptionRow";

function unwrapList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function pickEmployee(source = {}) {
  const employee = source.employee && typeof source.employee === "object" ? source.employee : {};
  const firstName =
    source.first_name || source.firstName || employee.first_name || employee.firstName || "";
  const lastName =
    source.last_name || source.lastName || employee.last_name || employee.lastName || "";
  const avatar =
    source.employee_avatar ||
    source.avatar ||
    employee.avatar ||
    employee.avatarUrl ||
    employee.picture ||
    "";
  return { firstName, lastName, avatar };
}

export function mapPositionHeader(position = {}) {
  return {
    title: position.name || position.title || "",
    code: formatPositionCode(position.code_str || position.code),
    status: position.status || position.current_status || "active",
  };
}

/**
 * Build the version row(s) for one subscription.
 *
 * ER-11223: the version row must use the version's OWN effective window
 * (`version_effective_from/to`), not the subscription window, so
 * PositionSubscriptionHistoryRow derives Past/Current/Future per version.
 * The backend exposes the subscription's current version as flat fields on
 * the subscription payload (version_number / version_effective_from /
 * version_effective_to; min/max are already the version's pay band). Older
 * payloads without version fields fall back to the subscription window so a
 * single-version subscription keeps rendering as before.
 */
export function mapCardVersion(mapped = {}) {
  // ER-11717: remove flat version_* fallbacks once those scalars are deleted.
  return {
    ordinal: mapped.versionNumber ?? mapped.version_number ?? 1,
    start:
      mapped.versionEffectiveFrom ??
      mapped.effectiveFrom ??
      mapped.effective_from,
    end: mapped.versionEffectiveTo ?? mapped.effectiveTo ?? null,
    payBandMin: mapped.minAmount ?? mapped.min_amount,
    payBandMax: mapped.maxAmount ?? mapped.max_amount,
  };
}

export function mapCardSubscriptions(payload) {
  return unwrapList(payload).map((raw) => {
    const mapped = mapSubscription(raw) || {};
    const versions =
      Array.isArray(mapped.versions) && mapped.versions.length
        ? mapped.versions.map(mapCardVersion)
        : [mapCardVersion(mapped)];
    return {
      id: mapped.id,
      code: `SUB-${mapped.id}`,
      title: mapped.payRangeName || mapped.payRangeCode || "",
      start: mapped.effectiveFrom,
      end: mapped.effectiveTo,
      versions,
      coverageGaps: mapped.coverageGaps || [],
    };
  });
}

export function mapCardAssignments(payload, occupantFallback = null) {
  const rows = unwrapList(payload)
    .filter((item) => {
      const commitment = String(item.commitment || item.commitment_level || "Primary");
      return commitment.toLowerCase() === "primary";
    })
    .map((item) => {
      const { firstName, lastName, avatar } = pickEmployee(item);
      const fte =
        item.fte ??
        (item.allocation === 1 || item.allocation === 100
          ? 1
          : item.allocation === 0.5 || item.allocation === 50
            ? 0.5
            : 1);
      return {
        id: item.id || item.assignment_id,
        firstName,
        lastName,
        avatar,
        fte,
        start: item.assigned_on || item.assigned_on_date || item.effective_on || item.effectiveOn,
        end: item.relieved_on || item.removed_on || item.relieved_on_date || null,
      };
    })
    .filter((row) => row.firstName || row.lastName);

  if (rows.length) return rows;
  if (!occupantFallback) return [];
  const { firstName, lastName, avatar } = pickEmployee(occupantFallback);
  if (!firstName && !lastName) return [];
  return [
    {
      id: occupantFallback.id || "occupant",
      firstName,
      lastName,
      avatar,
      fte: occupantFallback.fte ?? 1,
      start: occupantFallback.start || occupantFallback.assigned_on || null,
      end: occupantFallback.end || occupantFallback.relieved_on || null,
    },
  ];
}
